import { memo } from 'react';

import { Dialog, DialogClose } from '../Dialog';
import { DialogContent, DialogFooter, DialogTitle } from '../DialogLayout';
import { Button, ButtonProps } from '../Button';
import { cn } from '../../lib/styles';

export interface ConfirmationDialogProps {
  title: React.ReactNode;
  content?: React.ReactNode;
  open?: boolean;
  trigger?: React.ReactNode;
  cancelButtonProps?: ButtonProps & { text?: string };
  confirmButtonProps?: ButtonProps & { text?: string };
  onCancel?: () => void;
  onConfirm?: () => void;
  className?: string;
  overlayClassName?: string;
  titleClassName?: string;
  contentClassName?: string;
  footerClassName?: string;
  minWidth?: string;
}

const ConfirmationDialogBase = ({
  open,
  title,
  content,
  trigger,
  cancelButtonProps = {},
  confirmButtonProps = {},
  className,
  overlayClassName,
  titleClassName,
  contentClassName,
  footerClassName,
  minWidth = 'min-w-[420px]',
  onConfirm,
  onCancel,
}: ConfirmationDialogProps) => {
  const {
    text: cancelText = 'Cancel',
    variant: cancelVariant = 'secondary',
    size: cancelSize = 'lg',
    color: cancelColor = 'default',
    className: cancelClassName,
    ...cancelButtonRest
  } = cancelButtonProps;

  const {
    text: confirmText = 'Confirm',
    variant: confirmVariant = 'secondary',
    size: confirmSize = 'lg',
    color: confirmColor = 'destructive',
    className: confirmClassName,
    ...confirmButtonRest
  } = confirmButtonProps;

  const handleCancel = () => {
    onCancel?.();
  };

  const handleConfirm = () => {
    onConfirm?.();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onCancel?.();
    }
  };

  return (
    <Dialog
      overlayClassName={overlayClassName}
      className={className}
      trigger={trigger}
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DialogTitle className={cn('border-none pb-2 [&_h2]:normal-case!', minWidth, titleClassName)}>
        {title}
      </DialogTitle>

      {content ? (
        <DialogContent className={cn('pt-0 font-body-sm text-text-tertiary font-medium', contentClassName)}>
          {content}
        </DialogContent>
      ) : null}

      <DialogFooter className={cn('border-none', footerClassName)}>
        <DialogClose disabled={cancelButtonRest.disabled || cancelButtonRest.loading}>
          <Button
            variant={cancelVariant}
            size={cancelSize}
            color={cancelColor}
            className={cancelClassName}
            onClick={handleCancel}
            {...cancelButtonRest}
          >
            {cancelText}
          </Button>
        </DialogClose>

        <Button
          variant={confirmVariant}
          size={confirmSize}
          color={confirmColor}
          className={confirmClassName}
          onClick={handleConfirm}
          {...confirmButtonRest}
        >
          {confirmText}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export const ConfirmationDialog = memo(ConfirmationDialogBase);
